import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, Loader2, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Catalog of add-on mods — the same menu Bimmworks, MHD, etc. offer.
// Free (routine) vs Upcharged (showier/more invasive). Prices kept in sync with
// backend MOD_UPCHARGES in /app/backend/server.py (authoritative via /api/mod-options/pricing).
const INTAKE_MANIFOLDS = [
  { value: 'stock_single',   label: 'Stock Single-Stage Intake' },
  { value: 'three_stage',    label: '3-Stage (3SIM) — factory or retrofit' },
  { value: 'n54_im',         label: 'N54 Intake Manifold swap' },
  { value: 'velocity_stacks',label: 'Velocity Stacks (Alpha-N — no MAF)' },
  { value: 'other',          label: 'Other — describe in notes' },
];

// Fallback pricing if /api/mod-options/pricing is unreachable. Must match backend.
const FALLBACK_PRICING = {
  cold_start_delete: 0,
  secondary_o2_delete: 0,
  dtc_off: 0,
  hardcut_limiter: 15,
  catless_no_cel: 20,
  custom_rev: 20,
  top_speed_delete: 20,
  exhaust_burbles: 25,
  ghost_cams: 30,
};

const MOD_OPTIONS = [
  { id: 'cold_start_delete',  label: 'Cold-start delete',             help: 'Removes cold-start enrichment + cold cat heating. Quieter & cleaner startup. May fail emissions in colder climates.' },
  { id: 'secondary_o2_delete',label: 'Secondary O2 sensor delete',    help: 'Downstream lambda disabled. Often needed with catless headers or high-flow cats.' },
  { id: 'dtc_off',            label: 'Cosmetic DTCs off',             help: 'Only nuisance codes (catless, O2 delete, etc.). Safety-critical codes (knock, misfire, ABS) stay ON.' },
  { id: 'hardcut_limiter',    label: 'Hardcut rev limiter',           help: 'Spark-cut hard limiter (pop, pop, pop) instead of the factory soft fuel cut.' },
  { id: 'catless_no_cel',     label: 'Catless headers — no CEL',      help: 'O2 readiness + P0420/P0430 suppressed. Honest about it: emissions compliance becomes your responsibility.' },
  { id: 'custom_rev',         label: 'Custom rev limiter',            help: 'Default is 7200. Tell us in notes if you want 7400, 7500, etc. Only push this if you know your rotating assembly can take it.' },
  { id: 'top_speed_delete',   label: 'Top-speed governor removal',    help: 'OEM 155 mph governor removed. You own what happens next.' },
  { id: 'exhaust_burbles',    label: 'Exhaust burbles / decel pops',  help: 'Fuel + ignition retard on decel. Tunable volume. Some people love it, some people hate it.' },
  { id: 'ghost_cams',         label: 'Ghost cams (lumpy idle)',       help: 'VANOS overlap + idle spark/fuel scatter for a lumpy aggressive-sounding idle.' },
];

const formatPrice = (n) => {
  const val = Number(n) || 0;
  if (val === 0) return 'FREE';
  return `+$${val.toFixed(val % 1 ? 2 : 0)}`;
};

const parseBasePrice = (priceStr) => {
  if (!priceStr) return 0;
  const m = String(priceStr).replace(/,/g, '').match(/\$?([\d.]+)/);
  return m ? parseFloat(m[1]) : 0;
};

export default function ModOptionsDialog({ open, onClose, onConfirm, packageName, packagePrice, submitting }) {
  const [vin, setVin] = useState('');
  const [intake, setIntake] = useState('stock_single');
  const [mods, setMods] = useState([]);
  const [notes, setNotes] = useState('');
  const [helpId, setHelpId] = useState(null);
  const [pricing, setPricing] = useState(FALLBACK_PRICING);

  // Fetch authoritative mod pricing from backend on open (falls back silently on error).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/mod-options/pricing`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.upcharges) setPricing({ ...FALLBACK_PRICING, ...data.upcharges });
      } catch (err) {
        console.warn('[ModOptionsDialog] Could not fetch live pricing, using fallback:', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const basePrice = parseBasePrice(packagePrice);
  const upchargeTotal = mods.reduce((sum, id) => sum + (pricing[id] || 0), 0);
  const grandTotal = basePrice + upchargeTotal;

  const toggleMod = (id) => {
    setMods((prev) => prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]);
  };

  const handleConfirm = () => {
    onConfirm({
      vin: vin.trim().toUpperCase(),
      intake,
      mods,
      notes: notes.trim(),
    });
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={onClose}
          data-testid="mod-options-dialog"
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="my-auto w-full max-w-2xl bg-[#0F0F0F] border border-[#27272A] rounded-sm shadow-[0_0_60px_rgba(255,94,26,0.15)]"
          >
            {/* Header */}
            <div className="flex items-start justify-between p-5 border-b border-[#27272A]">
              <div>
                <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-1">// Customize your tune</div>
                <h2 className="font-['Outfit'] text-xl font-black text-white">{packageName || 'Tune Package'}</h2>
                <p className="text-xs text-zinc-400 mt-1">
                  Routine mods included free. Showier mods are a small upcharge — totals update live below.
                </p>
              </div>
              <button
                onClick={onClose}
                data-testid="mod-options-close"
                className="text-zinc-500 hover:text-white transition-colors p-1"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* VIN */}
              <div>
                <label className="block text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">
                  VIN (17 chars) — optional but recommended
                </label>
                <input
                  type="text"
                  maxLength={17}
                  value={vin}
                  onChange={(e) => setVin(e.target.value)}
                  placeholder="WBA..."
                  data-testid="mod-vin"
                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-sm px-3 py-2 text-sm text-white placeholder-zinc-600 font-['JetBrains_Mono'] focus:outline-none focus:border-[#FF5E1A]/60"
                />
              </div>

              {/* Intake manifold */}
              <div>
                <label className="block text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">
                  Intake Manifold
                </label>
                <select
                  value={intake}
                  onChange={(e) => setIntake(e.target.value)}
                  data-testid="mod-intake"
                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF5E1A]/60"
                >
                  {INTAKE_MANIFOLDS.map((im) => (
                    <option key={im.value} value={im.value}>{im.label}</option>
                  ))}
                </select>
              </div>

              {/* Mod checkboxes */}
              <div>
                <label className="block text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.18em] text-zinc-500 mb-2">
                  Mod Options — <span className="text-emerald-400">FREE</span> mods bundled, upcharges shown per line
                </label>
                <div className="space-y-2">
                  {MOD_OPTIONS.map((m) => {
                    const on = mods.includes(m.id);
                    const price = pricing[m.id] ?? 0;
                    const isFree = price === 0;
                    return (
                      <div key={m.id} className="border border-[#27272A] rounded-sm bg-[#0A0A0A] hover:border-[#FF5E1A]/40 transition-colors">
                        <button
                          type="button"
                          onClick={() => toggleMod(m.id)}
                          data-testid={`mod-toggle-${m.id}`}
                          className="w-full flex items-start gap-3 p-3 text-left"
                        >
                          <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-sm border flex items-center justify-center ${
                            on ? 'bg-[#FF5E1A] border-[#FF5E1A]' : 'bg-transparent border-[#3a3a3d]'
                          }`}>
                            {on && <CheckCircle2 className="w-3.5 h-3.5 text-[#0A0A0A]" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm text-white font-medium">{m.label}</span>
                              <span
                                data-testid={`mod-price-${m.id}`}
                                className={`text-[10px] font-['JetBrains_Mono'] px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${
                                  isFree
                                    ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
                                    : 'bg-[#FF5E1A]/10 border border-[#FF5E1A]/30 text-[#FF5E1A]'
                                }`}
                              >
                                {formatPrice(price)}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setHelpId(helpId === m.id ? null : m.id); }}
                                className="text-zinc-500 hover:text-[#FF5E1A] transition-colors"
                                aria-label="What's this?"
                              >
                                <Info className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            {helpId === m.id && (
                              <p className="text-[11px] text-zinc-400 mt-1.5 leading-relaxed">{m.help}</p>
                            )}
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">
                  Notes — anything else we should know?
                </label>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Injector size, custom rev limiter, E85 ratio, build details, etc."
                  data-testid="mod-notes"
                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-sm px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF5E1A]/60 resize-none"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-t border-[#27272A] bg-[#0A0A0A]">
              <div className="text-xs text-zinc-400 flex flex-col gap-0.5">
                <div>
                  <span className="font-['JetBrains_Mono'] text-zinc-500">Base:</span>{' '}
                  <span className="text-white">{packagePrice || `$${basePrice.toFixed(2)}`}</span>
                  {upchargeTotal > 0 && (
                    <>
                      <span className="mx-1.5 text-zinc-600">+</span>
                      <span className="text-[#FF5E1A] font-['JetBrains_Mono']" data-testid="mod-upcharge-total">
                        ${upchargeTotal.toFixed(2)} mods
                      </span>
                    </>
                  )}
                </div>
                <div className="font-['JetBrains_Mono']">
                  <span className="text-zinc-500">Total:</span>{' '}
                  <span className="text-white font-bold text-sm" data-testid="mod-grand-total">
                    ${grandTotal.toFixed(2)}
                  </span>
                  {mods.length > 0 && (
                    <span className="ml-2 text-emerald-400 text-[10px]">
                      {mods.length} mod{mods.length === 1 ? '' : 's'} added
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="mod-options-cancel"
                  className="px-4 py-2 text-xs font-bold tracking-wider uppercase text-zinc-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={submitting}
                  data-testid="mod-options-confirm"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF5E1A] hover:bg-[#FF7A42] disabled:opacity-60 text-white text-xs font-bold tracking-[0.12em] uppercase rounded-sm transition-all"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : 'Continue to checkout'}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
